import { magnifyQuantity } from '@diamond/shared/utils';

import type { CreateVoucherPayload, CreateVoucherRequest } from '../types';

const isValidNumber = (value: unknown): value is number =>
  typeof value === 'number' && !Number.isNaN(value);

const normalizeNumber = (value: unknown): number | null =>
  isValidNumber(value) ? value : null;

export const buildCreateVoucherPayload = (
  data: CreateVoucherRequest
): CreateVoucherPayload => {
  const {
    reward_configuration: rewardConfigForm,
    purchaseProducts = [],
    rewardDiscountProduct,
    rewardFreeProduct,
    codeValidated: _codeValidated,
    shipToCsv: _shipToCsv,
    target_configuration,
    acquisition_configuration,
    condition_configuration,
    ...rest
  } = data;

  const purchaseProductEntries = purchaseProducts
    .filter((product) => product?.value)
    .map((product) => ({
      itemCode: Number(product.value),
      minQuantity: normalizeNumber(product.min_quantity),
    }))
    .filter((product) => !Number.isNaN(product.itemCode));

  const normalizedMinTotalQuantity = normalizeNumber(
    rewardConfigForm.min_total_quantity_all_item
  );
  const isBundling = Boolean(rewardConfigForm.is_bundling);

  const rewardRuleProducts =
    purchaseProductEntries.length > 0
      ? purchaseProductEntries.map((product) => ({
          product_item_code: product.itemCode,
          min_quantity: magnifyQuantity(
            isBundling ? normalizedMinTotalQuantity : product.minQuantity
          ),
        }))
      : [];

  const discountProductItemCode =
    rewardDiscountProduct && rewardDiscountProduct.value !== ''
      ? Number(rewardDiscountProduct.value)
      : null;

  const freeGoodItemCode =
    rewardFreeProduct && rewardFreeProduct.value !== ''
      ? Number(rewardFreeProduct.value)
      : null;

  const hasValidDiscountProduct = isValidNumber(discountProductItemCode);
  const hasValidFreeGoodProduct = isValidNumber(freeGoodItemCode);

  const magnifiedMinTotalQuantity = isBundling
    ? magnifyQuantity(normalizedMinTotalQuantity)
    : null;

  const rewardConfigPayload: CreateVoucherPayload['reward_configuration'] = {
    reward_type: rewardConfigForm.reward_type,
    is_bundling: isBundling,
    min_total_quantity_all_item: magnifiedMinTotalQuantity,
    reward_rule_products: rewardRuleProducts,
  };

  if (
    rewardConfigForm.reward_type === 'DISCOUNT_AMOUNT' ||
    rewardConfigForm.reward_type === 'DISCOUNT_PERCENT'
  ) {
    rewardConfigPayload.discount_products = hasValidDiscountProduct
      ? [
          {
            product_item_code: discountProductItemCode as number,
            discount_amount:
              rewardConfigForm.reward_type === 'DISCOUNT_AMOUNT'
                ? rewardConfigForm.discount_amount_value ?? 0
                : null,
            discount_percentage:
              rewardConfigForm.reward_type === 'DISCOUNT_PERCENT'
                ? rewardConfigForm.discount_percentage_value ?? 0
                : null,
          },
        ]
      : [];
    rewardConfigPayload.free_goods = [];
  }

  if (rewardConfigForm.reward_type === 'FREE_GOODS') {
    // Apply quantity magnification for free goods quantity
    const magnifiedFreeGoodsQuantity = magnifyQuantity(
      rewardConfigForm.free_goods_quantity_value
    );

    rewardConfigPayload.free_goods =
      hasValidFreeGoodProduct &&
      isValidNumber(rewardConfigForm.free_goods_quantity_value) &&
      magnifiedFreeGoodsQuantity !== null
        ? [
            {
              product_item_code: freeGoodItemCode as number,
              quantity: magnifiedFreeGoodsQuantity,
            },
          ]
        : [];
    rewardConfigPayload.discount_products = [];
  }

  const targetConfigurationPayload: CreateVoucherPayload['target_configuration'] =
    {
      ...target_configuration,
      shipto_csv_url: target_configuration.shipto_csv_url || null,
      max_use_per_user: normalizeNumber(target_configuration.max_use_per_user),
    };

  const acquisitionConfigurationPayload: CreateVoucherPayload['acquisition_configuration'] =
    {
      ...acquisition_configuration,
      valid_day_since_account_signup: normalizeNumber(
        acquisition_configuration.valid_day_since_account_signup
      ),
    };

  const conditionConfigurationPayload: CreateVoucherPayload['condition_configuration'] =
    {
      ...condition_configuration,
      min_purchase_amount: normalizeNumber(
        condition_configuration.min_purchase_amount
      ),
    };

  return {
    ...rest,
    target_configuration: targetConfigurationPayload,
    acquisition_configuration: acquisitionConfigurationPayload,
    condition_configuration: conditionConfigurationPayload,
    reward_configuration: rewardConfigPayload,
  };
};
